/*
 * End of Quarter animation — native port of the Remotion "two-slide EOQ Reminder".
 * Intro card (End of Quarter Reminder + pill) flips in/out, then the drain slide:
 * a funding container drains (lime) while essentials boxes stack up (orange).
 *
 * Transparent background — the slideshow section supplies the full-width purple.
 * Content is sized to the container; text scales via --eoq-sc (renderedHeight / 1080),
 * mirroring the Remotion render. 30fps timeline, 288 frames (9.6s).
 *
 * When placed in the Advanced Slideshow and set to advance-on-finish, the animation
 * drives the parent <slide-show> to the next slide once it completes.
 */
(function () {
  'use strict';

  var FPS = 30;
  var TOTAL = 288;      // whole two-slide sequence
  var INTRO_LEN = 110;  // intro card length before the drain slide

  // brand palette (matches the Remotion build)
  var PURPLE_DK = '#3C2562';
  var LIME = '#D8F26A';
  var CORAL = '#FF791C';
  var CORAL_DK = '#D2620F';
  var LILAC = '#EECCFF';

  var SVGNS = 'http://www.w3.org/2000/svg';

  // ---------- easing ----------
  function clamp01(t) { return t < 0 ? 0 : t > 1 ? 1 : t; }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function easeInCubic(t) { return t * t * t; }
  function easeInOutCubic(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  // cubic-bezier(x1,y1,x2,y2) -> easing function of x
  function cubicBezier(x1, y1, x2, y2) {
    function A(a, b) { return 1 - 3 * b + 3 * a; }
    function B(a, b) { return 3 * b - 6 * a; }
    function C(a) { return 3 * a; }
    function calc(t, a, b) { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; }
    function slope(t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); }
    function tForX(x) {
      var t = x;
      for (var i = 0; i < 8; i++) {
        var s = slope(t, x1, x2);
        if (s === 0) return t;
        var xx = calc(t, x1, x2) - x;
        t -= xx / s;
      }
      return t;
    }
    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      return calc(tForX(x), y1, y2);
    };
  }
  var bezBoxX = cubicBezier(0.2, 0, 0, 1);
  var bezBoxY = cubicBezier(0.4, 0, 1, 1);

  // ---------- interpolate (piecewise, Remotion-style) ----------
  function interp(x, xs, ys, easing, clampL, clampR) {
    if (clampL === undefined) clampL = true;
    if (clampR === undefined) clampR = true;
    if (x <= xs[0]) return clampL ? ys[0] : ys[0];
    var last = xs.length - 1;
    if (x >= xs[last]) return clampR ? ys[last] : ys[last];
    var k = 0;
    while (k < last && x > xs[k + 1]) k++;
    var t = (x - xs[k]) / (xs[k + 1] - xs[k]);
    t = clamp01(t);
    if (easing) t = easing(t);
    return ys[k] + t * (ys[k + 1] - ys[k]);
  }

  // ---------- colour lerp across stops ----------
  function hexToRgb(h) {
    h = h.replace('#', '');
    return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)];
  }
  function rgbStr(c) { return 'rgb(' + Math.round(c[0]) + ',' + Math.round(c[1]) + ',' + Math.round(c[2]) + ')'; }
  function lerpColors(p, stops, cols) {
    var rgbs = cols.map(hexToRgb);
    if (p <= stops[0]) return rgbStr(rgbs[0]);
    var last = stops.length - 1;
    if (p >= stops[last]) return rgbStr(rgbs[last]);
    var k = 0;
    while (k < last && p > stops[k + 1]) k++;
    var t = (p - stops[k]) / (stops[k + 1] - stops[k]);
    var a = rgbs[k], b = rgbs[k + 1];
    return rgbStr([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1]), a[2] + t * (b[2] - a[2])]);
  }

  // ---------- spring (under-damped closed form) for the pill ----------
  function springValue(tSec, stiffness, damping, mass) {
    if (tSec <= 0) return 0;
    var w0 = Math.sqrt(stiffness / mass);
    var zeta = damping / (2 * Math.sqrt(stiffness * mass));
    if (zeta < 1) {
      var wd = w0 * Math.sqrt(1 - zeta * zeta);
      return 1 - Math.exp(-zeta * w0 * tSec) * (Math.cos(wd * tSec) + (zeta * w0 / wd) * Math.sin(wd * tSec));
    }
    return 1 - Math.exp(-w0 * tSec) * (1 + w0 * tSec);
  }

  // ---------- drain geometry (fixed 1080 square design) ----------
  var boxW = 300, boxH = 540, gap = 70, boxTop = 250, boxBot = boxTop + boxH; // 790
  var startX = (1080 - (boxW * 2 + gap)) / 2; // 205
  var fundX = startX, baskX = startX + boxW + gap; // 205, 575
  var rx = 22;
  var fundCx = fundX + boxW / 2;  // 355
  var baskCx = baskX + boxW / 2;  // 725
  var labelY = boxBot + 66;       // 856
  var cellPad = 16, colGap = 11;
  var colW = (boxW - cellPad * 2 - colGap * 2) / 3; // 82
  var shelfBottom = boxTop + boxH - cellPad;        // 774
  function colXLeft(c) { return baskX + cellPad + c * (colW + colGap); }
  function spanW(c0, c1) { return (c1 - c0 + 1) * colW + (c1 - c0) * colGap; }

  var PLACE = [[0, 1, 120], [2, 2, 150], [0, 0, 110], [1, 2, 120], [0, 1, 130], [2, 2, 120], [0, 0, 110], [1, 2, 110]];
  function packBoxes() {
    var surf = [shelfBottom, shelfBottom, shelfBottom];
    return PLACE.map(function (p) {
      var c0 = p[0], c1 = p[1], h = p[2], base = Infinity, c;
      for (c = c0; c <= c1; c++) base = Math.min(base, surf[c]);
      var top = base - h;
      for (c = c0; c <= c1; c++) surf[c] = top;
      var w = spanW(c0, c1);
      return { w: w, h: h, cx: colXLeft(c0) + w / 2, cy: top + h / 2 };
    });
  }

  var rxBox = 8, arcHeight = 120, travel = 28, spawnStart = 28, spawnStep = 13;

  // ---------- build the SVG scene ----------
  function el(tag, attrs) {
    var e = document.createElementNS(SVGNS, tag);
    for (var k in attrs) e.setAttribute(k, attrs[k]);
    return e;
  }

  function buildScene(root, label, uid) {
    var svg = el('svg', { viewBox: '0 0 1080 1080', preserveAspectRatio: 'xMidYMid meet', width: '100%', height: '100%' });
    svg.setAttribute('class', 'eoq__svg');

    var defs = el('defs', {});
    var clip = el('clipPath', { id: 'eoqFund-' + uid });
    clip.appendChild(el('rect', { x: fundX, y: boxTop, width: boxW, height: boxH, rx: rx }));
    defs.appendChild(clip);
    svg.appendChild(defs);

    // funding container
    svg.appendChild(el('rect', { x: fundX, y: boxTop, width: boxW, height: boxH, rx: rx, fill: PURPLE_DK }));
    var fillG = el('g', { 'clip-path': 'url(#eoqFund-' + uid + ')' });
    var fill = el('rect', { x: fundX, y: boxTop, width: boxW, height: boxH, fill: LIME });
    fillG.appendChild(fill);
    svg.appendChild(fillG);
    svg.appendChild(el('rect', { x: fundX, y: boxTop, width: boxW, height: boxH, rx: rx, fill: 'none', stroke: LILAC, 'stroke-width': 5 }));

    // basket container
    svg.appendChild(el('rect', { x: baskX, y: boxTop, width: boxW, height: boxH, rx: rx, fill: PURPLE_DK }));
    var boxesG = el('g', {});
    svg.appendChild(boxesG);
    svg.appendChild(el('rect', { x: baskX, y: boxTop, width: boxW, height: boxH, rx: rx, fill: 'none', stroke: LILAC, 'stroke-width': 5 }));

    // labels
    var lblFund = el('text', { x: fundCx, y: labelY, dx: 4, fill: LILAC, 'font-family': "'Inclusive Sans', sans-serif", 'font-size': 34, 'letter-spacing': 8, 'text-anchor': 'middle' });
    lblFund.textContent = label;
    var lblEss = el('text', { x: baskCx, y: labelY, dx: 4, fill: LILAC, 'font-family': "'Inclusive Sans', sans-serif", 'font-size': 34, 'letter-spacing': 8, 'text-anchor': 'middle' });
    lblEss.textContent = 'Essentials';
    svg.appendChild(lblFund);
    svg.appendChild(lblEss);

    // 8 boxes (one <rect> each), positioned every frame
    var boxes = packBoxes();
    var rects = boxes.map(function () {
      var r = el('rect', { rx: rxBox });
      boxesG.appendChild(r);
      return r;
    });

    root.appendChild(svg);
    return { fill: fill, boxes: boxes, rects: rects };
  }

  // ---------- per-frame render ----------
  function renderDrain(scene, L) {
    // funding drains from full to ~8% over frames 0..150, holds after
    var level = interp(L, [0, 150, 190], [1, 0.08, 0.08], easeInOutCubic);
    var fH = Math.max(0, boxH * level);
    scene.fill.setAttribute('y', boxBot - fH);
    scene.fill.setAttribute('height', fH);

    scene.boxes.forEach(function (b, i) {
      var r = scene.rects[i];
      var start = spawnStart + i * spawnStep;
      var arrival = start + travel;
      if (L < start) { r.setAttribute('opacity', 0); return; }

      if (L < arrival) {
        var p = clamp01((L - start) / (arrival - start));
        var x = interp(bezBoxX(p), [0, 1], [fundCx, b.cx]);
        var y = interp(bezBoxY(p), [0, 1], [boxTop - 10, b.cy]) - Math.sin(p * Math.PI) * arcHeight;
        var rot = interp(bezBoxX(p), [0, 1], [0, -360]);
        var op = interp(p, [0, 0.06], [0, 1]);
        var sc = interp(p, [0, 0.45], [0.9, 1], easeOutCubic);
        r.setAttribute('opacity', op);
        r.setAttribute('x', -b.w / 2); r.setAttribute('y', -b.h / 2);
        r.setAttribute('width', b.w); r.setAttribute('height', b.h);
        r.setAttribute('fill', lerpColors(p, [0, 0.2, 0.65, 1], [LIME, LIME, CORAL, CORAL]));
        r.setAttribute('stroke', lerpColors(p, [0, 0.35, 0.8, 1], [LILAC, LILAC, CORAL_DK, CORAL_DK]));
        r.setAttribute('stroke-width', interp(p, [0, 1], [5, 3]));
        r.setAttribute('transform', 'translate(' + x + ' ' + y + ') rotate(' + rot + ') scale(' + sc + ')');
      } else {
        var pop = interp(L, [arrival, arrival + 5, arrival + 12], [1.08, 0.98, 1]);
        r.setAttribute('opacity', 1);
        r.setAttribute('x', -b.w / 2); r.setAttribute('y', -b.h / 2);
        r.setAttribute('width', b.w); r.setAttribute('height', b.h);
        r.setAttribute('fill', CORAL);
        r.setAttribute('stroke', CORAL_DK);
        r.setAttribute('stroke-width', 3);
        r.setAttribute('transform', 'translate(' + b.cx + ' ' + b.cy + ') scale(' + pop + ')');
      }
    });
  }

  function flipTransform(rotY) { return 'rotateY(' + rotY + 'deg)'; }

  function renderFrame(inst, g) {
    var introInner = inst.introInner, slideInner = inst.slideInner;

    if (g < INTRO_LEN) {
      inst.intro.style.visibility = 'visible';
      inst.slide.style.visibility = 'hidden';
      // intro flip in [0,16], flip out [90,106]
      var iRot = g < 16 ? interp(g, [0, 16], [-90, 0], easeOutCubic) : (g > 90 ? interp(g, [90, 106], [0, 90], easeInCubic) : 0);
      var iOp = Math.min(interp(g, [0, 8], [0, 1]), interp(g, [90, 104], [1, 0]));
      introInner.style.transform = flipTransform(iRot);
      introInner.style.opacity = iOp;
      // pill spring in at frame 24
      var sp = springValue((g - 24) / FPS, 120, 12, 0.8);
      var pillScale = interp(sp, [0, 1], [0.6, 1]);
      var pillOp = interp(g, [24, 31], [0, 1]);
      inst.pill.style.transform = 'scale(' + pillScale + ')';
      inst.pill.style.opacity = pillOp;
    } else {
      inst.intro.style.visibility = 'hidden';
      inst.slide.style.visibility = 'visible';
      var L = g - INTRO_LEN; // slide local
      // flip in only — no end flip-out, so the final state holds static and the
      // slideshow's own transition handles the change to the next slide (no clash)
      var sRot = L < 16 ? interp(L, [0, 16], [-90, 0], easeOutCubic) : 0;
      var sOp = interp(L, [0, 8], [0, 1]);
      slideInner.style.transform = flipTransform(sRot);
      slideInner.style.opacity = sOp;
      renderDrain(inst.scene, L);
    }
  }

  function renderStatic(inst) {
    // reduced-motion: show the settled end state (drain low, boxes stacked, no flip)
    inst.intro.style.visibility = 'hidden';
    inst.slide.style.visibility = 'visible';
    inst.slideInner.style.transform = 'none';
    inst.slideInner.style.opacity = 1;
    renderDrain(inst.scene, 150);
  }

  // ---------- slideshow integration ----------
  function findSlideShow(node) {
    var n = node;
    while (n && n !== document.body) {
      if (n.tagName && n.tagName.toLowerCase() === 'slide-show') return n;
      n = n.parentNode;
    }
    return null;
  }
  function findSlide(node) {
    var n = node;
    while (n && n !== document.body) {
      if (n.classList && n.classList.contains('slideshow__slide')) return n;
      n = n.parentNode;
    }
    return null;
  }

  function advanceSlideshow(inst) {
    var ss = inst.slideShow;
    if (!ss) return;
    // cancel any pending autoplay timer so only the animation drives this advance
    try { if (ss.autoplayTimer) clearTimeout(ss.autoplayTimer); } catch (e) {}
    if (typeof ss.showNextSlide === 'function') ss.showNextSlide();
  }

  // ---------- playback ----------
  function play(inst) {
    if (inst.reduced) { renderStatic(inst); if (inst.advance && inst.slideShow) { setTimeout(function () { advanceSlideshow(inst); }, 4000); } return; }
    cancelAnimationFrame(inst.raf);
    inst.startTime = null;
    var step = function (now) {
      if (inst.startTime === null) inst.startTime = now;
      // while this animation is driving the advance, keep the section's own autoplay
      // timer (which may be shorter than the animation) from pre-empting it
      if (inst.advance && inst.slideShow && inst.slideShow.autoplayTimer) {
        try { clearTimeout(inst.slideShow.autoplayTimer); } catch (e) {}
      }
      var g = (now - inst.startTime) / 1000 * FPS;
      if (g >= TOTAL) {
        // play through exactly once, then stop on the final frame (never loop).
        // it replays only when the slide becomes active again.
        renderFrame(inst, TOTAL - 0.001);
        if (inst.advance && inst.slideShow) advanceSlideshow(inst);
        return;
      }
      renderFrame(inst, g);
      inst.raf = requestAnimationFrame(step);
    };
    inst.raf = requestAnimationFrame(step);
  }

  function stop(inst) { cancelAnimationFrame(inst.raf); }

  // ---------- init ----------
  function init(root) {
    if (root.dataset.eoqReady === '1') return;
    root.dataset.eoqReady = '1';

    var uid = Math.random().toString(36).slice(2, 8);
    var inst = {
      root: root,
      advance: root.dataset.advance === 'true',
      reduced: window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      intro: root.querySelector('.eoq__intro'),
      introInner: root.querySelector('.eoq__intro-inner'),
      pill: root.querySelector('.eoq__pill'),
      slide: root.querySelector('.eoq__slide'),
      slideInner: root.querySelector('.eoq__slide-inner'),
      drain: root.querySelector('.eoq__drain'),
      raf: 0,
      startTime: null
    };
    inst.scene = buildScene(inst.drain, root.dataset.label || 'Funding', uid);
    inst.slideShow = findSlideShow(root);
    inst.slide$ = findSlide(root);

    // keep text scaled to the rendered height, like the Remotion render (height / 1080)
    var applyScale = function () {
      var h = root.clientHeight || root.getBoundingClientRect().height;
      if (h) root.style.setProperty('--eoq-sc', (h / 1080).toFixed(4));
    };
    applyScale();
    if (window.ResizeObserver) { inst.ro = new ResizeObserver(applyScale); inst.ro.observe(root); }
    else window.addEventListener('resize', applyScale);

    // render an initial frame so it's not blank before playing
    inst.reduced ? renderStatic(inst) : renderFrame(inst, 0);

    if (inst.slide$ && inst.slideShow) {
      // drive playback off the slide's active state
      var isActive = function () { return inst.slide$.classList.contains('is-active'); };
      // Only act on a REAL active<->inactive change. The slideshow toggles other
      // classes (e.g. 'transition-out') on the active slide mid-transition; reacting
      // to those would restart the animation from frame 0 as it transitions out.
      // On activate: reset to the start, then play. On deactivate: stop and freeze
      // on the final frame (no reset — that reset is what flashed the start).
      var wasActive = false;
      var sync = function () {
        var active = isActive();
        if (active === wasActive) return;
        wasActive = active;
        if (active) { if (!inst.reduced) renderFrame(inst, 0); play(inst); }
        else { stop(inst); }
      };
      inst.mo = new MutationObserver(sync);
      inst.mo.observe(inst.slide$, { attributes: true, attributeFilter: ['class'] });
      sync();
    } else {
      // standalone (no slideshow): play only while on screen
      if (window.IntersectionObserver) {
        inst.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) play(inst); else stop(inst); });
        }, { threshold: 0.25 });
        inst.io.observe(root);
      } else { play(inst); }
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.eoq[data-eoq]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else { initAll(); }

  // re-init inside the Shopify theme editor when a section is re-rendered
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.EOQAnimation = { initAll: initAll };
})();
